import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Suggestion } from "@/types/document";
import { Check, Trash2, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";

interface RubricBoardItemProps {
  suggestion: Suggestion;
  isLoading: boolean;
  isSelected: boolean;
  isReferencePanelVisible: boolean;
  hint?: boolean;
  onApprove: (suggestion: Suggestion) => void;
  onReject: (suggestion: Suggestion) => void;
  onSelect: (suggestion: Suggestion) => void;
  onViewBlocks: (suggestion: Suggestion) => void;
  onEdit: (suggestion: Suggestion) => void;
}

const getSentimentColor = (
  sentiment: string,
  status: string,
  isSelected: boolean,
  isHovered: boolean
) => {
  const isApproved = status === "approved";
  const isHighlighted = isSelected || isHovered;

  switch (sentiment) {
    case "positive":
      if (isApproved) {
        return "border-positive bg-positive";
      }
      return isHighlighted
        ? "border-positive border-dashed bg-positive"
        : "border-positive border-dashed";
    case "negative":
      if (isApproved) {
        return "border-negative bg-negative";
      }
      return isHighlighted
        ? "border-negative border-dashed bg-negative"
        : "border-negative border-dashed";
    default:
      return isHighlighted ? "border-neutral bg-neutral" : "border-neutral";
  }
};

const getSentimentRingColor = (sentiment: string) => {
  switch (sentiment) {
    case "positive":
      return "ring-1 ring-green-300 ring-offset-2";
    case "negative":
      return "ring-1 ring-red-300 ring-offset-2";
    default:
      return "ring-1 ring-gray-300 ring-offset-2";
  }
};

export const RubricBoardItem = ({
  suggestion,
  isLoading,
  isSelected,
  isReferencePanelVisible,
  hint,
  onApprove,
  onReject,
  onSelect,
  onViewBlocks,
  onEdit,
}: RubricBoardItemProps) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div data-test-role="rubric-board-item">
      <div className="flex w-full">
        <Card
          data-test-role="rubric-board-item-card"
          className={cn(
            getSentimentColor(
              suggestion.sentiment,
              suggestion.status,
              isSelected,
              isHovered
            ),
            "border py-0 flex-1 cursor-pointer transition-all",
            isSelected && getSentimentRingColor(suggestion.sentiment)
          )}
          onClick={() => onSelect(suggestion)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <CardContent className="p-2">
            <p className="text-xs text-gray-700">{suggestion.text}</p>
          </CardContent>
        </Card>
        <div
          id={hint ? "hint-approve-suggestion" : suggestion.id}
          className="flex flex-col width-[40px] justify-center ml-3 gap-2"
        >
          {suggestion.status === "pending" && (
            <Button
              variant="outline"
              aria-label="Approve suggestion"
              size="icon"
              className="h-7 w-7 rounded-sm border-positive bg-positive text-positive hover:text-green-700 hover:bg-green-100"
              onClick={(e) => {
                e.stopPropagation();
                onApprove(suggestion);
              }}
              disabled={isLoading}
            >
              <Check className="h-4 w-4" />
            </Button>
          )}
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7 rounded-sm border-negative bg-negative text-negative hover:text-red-700 hover:bg-red-50"
            onClick={(e) => {
              e.stopPropagation();
              onReject(suggestion);
            }}
            aria-label="Reject suggestion"
            disabled={isLoading}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="flex justify-between mr-10 mt-2">
        <Button
          variant="link"
          className="text-xs px-0 h-6 hover:no-underline"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(suggestion);
          }}
        >
          Edit
        </Button>
        <div>
          <Badge
            variant={
              isSelected && isReferencePanelVisible ? "default" : "secondary"
            }
            className="border cursor-pointer"
            role="button"
            onClick={() => onViewBlocks(suggestion)}
          >
            {isSelected && isReferencePanelVisible ? (
              <>
                Hide blocks
                <XCircle className="h-3 w-3 ml-1" />
              </>
            ) : (
              "View Blocks"
            )}
          </Badge>
        </div>
      </div>
    </div>
  );
};
